import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Bell, Flag, AlertTriangle, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationBell({ pursuitId, onViewRedFlags }) {
  const { getAuthHeaders } = useAuth();
  const [count, setCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  
  const fetchNotifications = useCallback(async () => {
    if (!pursuitId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/pursuits/${pursuitId}/notifications`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setCount(data.unacknowledged_red_flags || 0);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, [pursuitId, getAuthHeaders]);

  useEffect(() => {
    fetchNotifications();
    
    // Poll every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="relative h-9 w-9 p-0"
          data-testid="notification-bell"
        >
          <Bell className={cn(
            "w-5 h-5",
            count > 0 ? "text-slate-700" : "text-slate-400"
          )} />
          
          {/* Red dot indicator */}
          {count > 0 && (
            <span 
              className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center"
              data-testid="notification-badge"
            >
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                {count > 9 ? '9+' : count}
              </span>
            </span>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent 
        className="w-72 p-0" 
        align="end"
        data-testid="notification-popover"
      >
        <div className="p-3 border-b border-slate-100">
          <h4 className="font-semibold text-sm text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Notifications
          </h4>
        </div>
        
        <div className="p-3">
          {count > 0 ? (
            <div className="flex items-start gap-3 p-2 bg-red-50 rounded-lg border border-red-100">
              <div className="p-1.5 bg-red-100 rounded">
                <Flag className="w-4 h-4 text-red-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">
                  {count} Unacknowledged Red Flag{count !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-red-600 mt-0.5">
                  Review and acknowledge pending alerts
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 h-7 text-xs border-red-200 text-red-700 hover:bg-red-100"
                  onClick={() => {
                    setIsOpen(false);
                    onViewRedFlags?.();
                  }}
                >
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  View Red Flags
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center py-4 text-center">
              <div className="w-10 h-10 bg-emerald-50 rounded-full flex items-center justify-center mb-2">
                <Check className="w-5 h-5 text-emerald-600" />
              </div>
              <p className="text-sm text-slate-600">All clear!</p>
              <p className="text-xs text-slate-400 mt-0.5">
                No pending notifications
              </p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
